import { Component, Input } from '@angular/core';
import { AGU_ICONS, AGUIconDefinition, AGUIconName } from './icon-paths';

/**
 * One of the mockup's inline stroke SVGs, at the size the mockup gives it (19px nav, 15px tile chip, 13px button glyph).
 *
 * The glyph's fill is a custom-property API rather than something a consumer
 * styles directly. The mockup fills the ACTIVE nav glyph as well as stroking it
 * (`.menu a svg { fill:currentColor; fill-opacity:0 }` / `.menu a.on svg:not(.extico)
 * { fill-opacity:.18 }`, home.html:45,52,54), and a consumer's own
 * `... agu-icon svg { fill-opacity: .18 }` CANNOT reach this template: Angular's
 * emulated encapsulation stamps the consumer's `_ngcontent-*` attribute onto its
 * compiled selector, and the `<svg>` below belongs to THIS component, so the
 * selector never matches (measured live on the sidebar: `fillOpacity` → `1`,
 * Suite P P-SB8). Custom properties inherit straight through that boundary, so a
 * consumer sets `--agu-icon-fill-opacity` on (or above) the `agu-icon` host and
 * the rule here picks it up.
 */
@Component({
    standalone: false,
    selector: 'agu-icon',
    templateUrl: './icon.component.html',
    // `fill="none"` on the template's <svg> is a presentation attribute, which
    // any CSS declaration outranks — so the default of 0 has to reproduce it
    // exactly, and does: a currentColor fill at zero opacity paints nothing.
    // `.agu-icon__svg--solid` is the active nav glyph (P/Member_Hub.html:1847-1885, `.menu a.on svg.solid { fill-opacity:1 }`);
    // `fill-opacity .18s` is the bookmark's fill ease (:1770-1790) and is dropped under reduced motion.
    styles: [
        'svg { fill: currentColor; fill-opacity: var(--agu-icon-fill-opacity, 0); }',
        'svg { transition: fill-opacity .18s; }',
        'svg.agu-icon__svg--solid { fill-opacity: 1; }',
        '@media (prefers-reduced-motion: reduce) { svg { transition: none; } }',
    ],
    host: { class: 'agu-icon' },
})
export class AGUIconComponent {
    @Input({ required: true }) Name!: AGUIconName;
    @Input() Size = 19;
    /**
     * Paint the glyph's solid form (the active nav item). Glyphs with no `SolidPaths` ignore it and stay outlined, so a caller can bind it
     * unconditionally; the knocked-out details take `--agu-icon-knock`, which the caller sets to the surface behind the glyph.
     */
    @Input() Solid = false;

    /** True when `Solid` was asked for and this glyph has a solid form. */
    get IsSolid(): boolean {
        return this.Solid && (this.Icon.SolidPaths?.length ?? 0) > 0;
    }

    get Icon(): AGUIconDefinition {
        return AGU_ICONS[this.Name];
    }
}
